/**
 * Approach 1: Direct Chart Click Selection
 *
 * This component overlays the ECG chart and handles cycle selection
 * by detecting clicks on R-peak markers. It shows a floating panel
 * with selection confirmation and "Analyze" action.
 *
 * Usage:
 * <CycleClickSelector
 *   chartRef={highchartsRef}
 *   rPeaks={[{ number: 1, timeMs: 1234, ... }, ...]}
 *   onCycleSelect={(cycleNumber, cycleData) => handleSelection(cycleNumber, cycleData)}
 *   selectedCycle={selectedCycleNumber}
 * />
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { highchartsLabelFormatterFromMs } from '../../../../utils/dateFormatter';
import styles from './CycleClickSelector.module.scss';

// Icons as inline SVG for portability
const AnalyzeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ClickIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
  </svg>
);

export default function CycleClickSelector({
  chartRef,
  rPeaks = [],
  onCycleSelect,
  onAnalyzeClick,
  selectedCycle = null,
  selectedCycleData: selectedCycleDataProp = null,
  showHint = true,
  isAnalyzing = false
}) {
  const { t } = useTranslation();
  const [hoveredCycle, setHoveredCycle] = useState(null);
  const [highlightPosition, setHighlightPosition] = useState(null);

  // Use passed cycle data or look up from rPeaks
  const selectedCycleData = selectedCycleDataProp || (selectedCycle !== null
    ? rPeaks.find(peak => peak.number === selectedCycle)
    : null);

  // Find adjacent peaks by time position (works for both numbered and edited markers)
  const findAdjacentPeaks = useCallback((cycleData) => {
    if (!cycleData || !cycleData.timeMs) return { prevPeak: null, nextPeak: null };

    // Sort peaks by time
    const sortedPeaks = [...rPeaks].sort((a, b) => a.timeMs - b.timeMs);

    // Find the current peak's position in the sorted list
    const currentIndex = sortedPeaks.findIndex(p => p.timeMs === cycleData.timeMs);

    const prevPeak = currentIndex > 0 ? sortedPeaks[currentIndex - 1] : null;
    const nextPeak = currentIndex < sortedPeaks.length - 1 ? sortedPeaks[currentIndex + 1] : null;

    return { prevPeak, nextPeak };
  }, [rPeaks]);

  // Calculate highlight band position based on selected cycle
  useEffect(() => {
    if (selectedCycleData && chartRef?.current?.chart) {
      const chart = chartRef.current.chart;
      const xAxis = chart.xAxis[0];

      // Find previous and next R-peak by time position
      const { prevPeak, nextPeak } = findAdjacentPeaks(selectedCycleData);

      // Calculate time boundaries (use half RR interval on each side if no adjacent peak)
      const cycleStartMs = prevPeak
        ? (prevPeak.timeMs + selectedCycleData.timeMs) / 2
        : selectedCycleData.timeMs - 400; // ~400ms before if no prev

      const cycleEndMs = nextPeak
        ? (selectedCycleData.timeMs + nextPeak.timeMs) / 2
        : selectedCycleData.timeMs + 400; // ~400ms after if no next

      // Convert to pixel positions
      const leftPx = xAxis.toPixels(cycleStartMs);
      const rightPx = xAxis.toPixels(cycleEndMs);

      if (!isNaN(leftPx) && !isNaN(rightPx)) {
        setHighlightPosition({
          left: leftPx,
          width: rightPx - leftPx
        });
      }
    } else {
      setHighlightPosition(null);
    }
  }, [selectedCycleData, chartRef, findAdjacentPeaks]);

  const handleAnalyze = useCallback(() => {
    if (selectedCycleData && onAnalyzeClick) {
      onAnalyzeClick(selectedCycle, selectedCycleData);
    }
  }, [selectedCycle, selectedCycleData, onAnalyzeClick]);

  const handleClearSelection = useCallback(() => {
    if (onCycleSelect) {
      onCycleSelect(null, null);
    }
  }, [onCycleSelect]);

  // Format time for display using existing utility function
  const formatTime = (ms) => {
    if (ms === undefined || ms === null) return '-';
    return highchartsLabelFormatterFromMs(ms, false);
  };

  // Calculate RR interval using time-based adjacent peak lookup
  const getRRInterval = () => {
    if (!selectedCycleData) return null;
    const { prevPeak } = findAdjacentPeaks(selectedCycleData);
    if (prevPeak) {
      return selectedCycleData.timeMs - prevPeak.timeMs;
    }
    return null;
  };

  const rrInterval = getRRInterval();

  return (
    <div className={styles.cycleSelectionOverlay}>
      {/* Cycle highlight band */}
      {highlightPosition && (
        <div
          className={styles.cycleHighlight}
          style={{
            left: `${highlightPosition.left}px`,
            width: `${highlightPosition.width}px`
          }}
        />
      )}

      {/* Selection panel - show when we have valid cycle data (timeMs) */}
      {selectedCycleData && selectedCycleData.timeMs && (
        <div className={styles.selectionPanel}>
          <button
            className={styles.closeButton}
            onClick={handleClearSelection}
            title={t('recordView.multiChart.ecg.cycleSelection.clearSelection', 'Clear selection')}
          >
            <CloseIcon />
          </button>

          <div className={styles.panelHeader}>
            <div className={styles.cycleIcon}>
              {selectedCycleData.title || '-'}
            </div>
            <div>
              <p className={styles.panelTitle}>
                {t('recordView.multiChart.ecg.cycleSelection.selectedCycle', 'Selected Cycle')}
              </p>
              <p className={styles.cycleNumber}>
                {selectedCycle !== null ? `#${selectedCycle}` : selectedCycleData.title || '-'}
              </p>
            </div>
          </div>

          <div className={styles.panelDivider} />

          <div className={styles.panelInfo}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                {t('recordView.multiChart.ecg.cycleSelection.rPeakTime', 'R-peak time')}
              </span>
              <span className={styles.infoValue}>
                {formatTime(selectedCycleData.timeMs)}
              </span>
            </div>
            {rrInterval && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {t('recordView.multiChart.ecg.cycleSelection.rrInterval', 'RR interval')}
                </span>
                <span className={styles.infoValue}>
                  {rrInterval.toFixed(0)} ms
                </span>
              </div>
            )}
          </div>

          <button
            className={styles.analyzeButton}
            onClick={handleAnalyze}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? (
              <span className={styles.spinner} />
            ) : (
              <AnalyzeIcon />
            )}
            {isAnalyzing
              ? t('consultation.diagnosticData.analyzing', 'Analyzing...')
              : t('recordView.multiChart.ecg.cycleSelection.analyzeThisCycle', 'Analyze This Cycle')}
          </button>
        </div>
      )}

      {/* Hint when no selection */}
      {!selectedCycleData && showHint && rPeaks.length > 0 && (
        <div className={styles.noSelectionHint}>
          <ClickIcon />
          {t('recordView.multiChart.ecg.cycleSelection.clickToSelect', 'Click near an R-peak to select a cycle for analysis')}
        </div>
      )}
    </div>
  );
}

/**
 * Hook to integrate cycle selection with Highcharts
 * Call this in the component that renders the ECG chart
 */
export function useCycleSelection(chartRef, rPeaks, onSelect) {
  const [selectedCycle, setSelectedCycle] = useState(null);

  useEffect(() => {
    if (!chartRef?.current?.chart) return;

    const chart = chartRef.current.chart;

    // Add click handler to annotations (R-peak markers)
    const handleAnnotationClick = (e) => {
      // Check if clicked element is an R-peak marker annotation
      const annotation = e.target.closest('.highcharts-annotation');
      if (annotation) {
        const cycleNumber = parseInt(annotation.dataset?.cycleNumber, 10);
        if (!isNaN(cycleNumber)) {
          setSelectedCycle(cycleNumber);
          const cycleData = rPeaks.find(p => p.number === cycleNumber);
          if (onSelect) onSelect(cycleNumber, cycleData);
        }
      }
    };

    // Attach event listener to chart container
    const container = chart.container;
    container.addEventListener('click', handleAnnotationClick);

    return () => {
      container.removeEventListener('click', handleAnnotationClick);
    };
  }, [chartRef, rPeaks, onSelect]);

  const clearSelection = useCallback(() => {
    setSelectedCycle(null);
    if (onSelect) onSelect(null, null);
  }, [onSelect]);

  return { selectedCycle, setSelectedCycle, clearSelection };
}
